import { useMemo, useState } from "react";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";

const CHIP = {
  scheduled: "bg-white text-[#4A0000] border-l-4 border-[#B08D57]",
  in_progress: "bg-[#B08D57]/20 text-[#1A1A1A] border-l-4 border-[#B8952E]",
  completed: "bg-[#4A0000]/10 text-[#4A0000] border-l-4 border-[#4A0000]",
  cancelled: "bg-red-50 text-red-700 border-l-4 border-red-500 line-through",
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a, b) => key(a) === key(b);

export default function ShootsCalendar({ properties, onOpen }) {
  const [mode, setMode] = useState("month");
  const [cursor, setCursor] = useState(() => {
    const dated = properties.filter((p) => p.shoot_date).map((p) => p.shoot_date.slice(0, 10)).sort();
    const now = new Date().toISOString().slice(0, 10);
    const upcoming = dated.find((d) => d >= now) || dated[0];
    return upcoming ? new Date(upcoming + "T00:00:00") : new Date();
  });

  const byDate = useMemo(() => {
    const m = {};
    properties.forEach((p) => {
      if (!p.shoot_date) return;
      const k = p.shoot_date.slice(0, 10);
      (m[k] = m[k] || []).push(p);
    });
    return m;
  }, [properties]);

  const days = useMemo(() => {
    if (mode === "week") {
      const start = new Date(cursor);
      start.setDate(cursor.getDate() - cursor.getDay());
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor, mode]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (mode === "week") d.setDate(cursor.getDate() + dir * 7);
    else d.setMonth(cursor.getMonth() + dir);
    setCursor(d);
  };

  const today = new Date();
  const label = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="mb-8 bg-white border border-[#E9DDC6] rounded-md overflow-hidden" data-testid="shoots-calendar">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E9DDC6] flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarBlank size={18} weight="duotone" color="#4A0000" />
          <span className="font-display text-xl">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-[#E9DDC6] overflow-hidden text-xs">
            {["week", "month"].map((m) => (
              <button key={m} data-testid={`cal-mode-${m}`} onClick={() => setMode(m)}
                className={`px-3 py-1.5 capitalize transition-colors ${mode === m ? "bg-[#4A0000] text-white" : "bg-white text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>{m}</button>
            ))}
          </div>
          <button data-testid="cal-today" onClick={() => setCursor(new Date())}
            className="text-xs px-3 py-1.5 rounded-md border border-[#E9DDC6] text-[#5C5A58] hover:bg-[#FAF9F6]">Today</button>
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="p-1.5 rounded-md border border-[#E9DDC6] hover:bg-[#FAF9F6]"><CaretLeft size={16} /></button>
          <button data-testid="cal-next" onClick={() => shift(1)} className="p-1.5 rounded-md border border-[#E9DDC6] hover:bg-[#FAF9F6]"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#E9DDC6] bg-[#FAF9F6]">
        {DOW.map((d) => <div key={d} className="px-2 py-2 text-xs font-semibold text-[#5C5A58] text-center">{d}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const items = byDate[key(d)] || [];
          const inMonth = mode === "week" || d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          return (
            <div key={i} data-testid={`cal-day-${key(d)}`}
              className={`min-h-[104px] border-b border-r border-[#F0ECE2] p-1.5 flex flex-col gap-1 ${inMonth ? "" : "bg-[#FBFAF7]"}`}>
              <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "bg-[#4A0000] text-white font-bold" : inMonth ? "text-[#1A1A1A]" : "text-[#C4BFB4]"}`}>{d.getDate()}</span>
              {items.map((p) => (
                <button key={p.id} data-testid={`cal-shoot-${p.id}`} onClick={() => onOpen(p.id)}
                  title={`${p.title} · ${p.address}`}
                  className={`text-left text-[11px] leading-tight rounded px-1.5 py-1 truncate hover:brightness-95 transition ${CHIP[p.status] || CHIP.scheduled}`}>
                  {p.title}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
